import { useEffect, useState, useCallback } from "react";
import { priceCompareAdminAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, MousePointerClick, Percent, SearchX, Tags, Bookmark, Monitor, Smartphone, TrendingUp, Store, Flame, Download } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
];

function Kpi({ icon: Icon, label, value, testid, accent }) {
  return (
    <Card className="border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className={`h-4 w-4 ${accent || "text-primary"}`} />
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-2 font-display text-2xl font-bold">{value}</p>
    </Card>
  );
}

function RankedList({ title, icon: Icon, rows, emptyText, renderLabel, renderMeta, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count || 0));
  return (
    <Card className="border-border bg-card p-4" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="space-y-1">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{renderLabel(r)}</span>
                <span className="shrink-0 font-mono text-xs font-bold text-foreground/80">{r.count}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary/60">
                <div className="h-full rounded-full bg-primary" style={{ width: `${((r.count || 0) / max) * 100}%` }} />
              </div>
              {renderMeta && renderMeta(r)}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PlatformSplit({ title, data, testid }) {
  const web = data?.web || 0;
  const mobile = data?.mobile || 0;
  const total = web + mobile;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <Card className="border-border bg-card p-4" data-testid={testid}>
      <h4 className="mb-3 text-sm font-semibold">{title}</h4>
      <div className="space-y-3">
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5 text-primary" /> Web</span>
            <span className="font-mono">{web} · {pct(web)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary/60"><div className="h-full bg-primary" style={{ width: `${pct(web)}%` }} /></div>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-accent" /> Mobile</span>
            <span className="font-mono">{mobile} · {pct(mobile)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary/60"><div className="h-full bg-accent" style={{ width: `${pct(mobile)}%` }} /></div>
        </div>
      </div>
    </Card>
  );
}

function TrendChart({ trend }) {
  if (!trend || trend.length === 0) {
    return <p className="text-xs text-muted-foreground">No activity in this range yet.</p>;
  }
  const max = Math.max(1, ...trend.map((d) => Math.max(d.searches || 0, d.clicks || 0)));
  return (
    <div>
      <div className="flex items-end gap-1" style={{ height: 120 }}>
        {trend.map((d, i) => (
          <div key={i} className="flex flex-1 items-end justify-center gap-0.5" title={`${d.date}: ${d.searches} searches, ${d.clicks} clicks`}>
            <div className="w-1/2 rounded-t bg-primary/70" style={{ height: `${((d.searches || 0) / max) * 100}%`, minHeight: d.searches ? 2 : 0 }} />
            <div className="w-1/2 rounded-t bg-accent/70" style={{ height: `${((d.clicks || 0) / max) * 100}%`, minHeight: d.clicks ? 2 : 0 }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-primary/70" /> Searches</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-accent/70" /> Clicks</span>
      </div>
    </div>
  );
}

export default function PriceCompareAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    priceCompareAdminAPI
      .getAnalytics(days)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await priceCompareAdminAPI.exportAnalytics(days);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `price-compare-analytics-${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Analytics exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="admin-pc-analytics">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold uppercase tracking-wider">Analytics</p>
          <p className="text-xs text-muted-foreground">Search & click-through performance for the Price Compare tool.</p>
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <Button key={r.id} size="sm" variant={days === r.id ? "default" : "outline"} onClick={() => setDays(r.id)} data-testid={`admin-pc-range-${r.id}`}>
              {r.label}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting || loading} className="ml-1" data-testid="admin-pc-export-csv">
            <Download className="mr-1 h-4 w-4" /> {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
      ) : !data ? (
        <Card className="border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">Could not load analytics.</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Kpi icon={Search} label="Searches" value={t.searches ?? 0} testid="admin-pc-kpi-searches" />
            <Kpi icon={MousePointerClick} label="Link clicks" value={t.clicks ?? 0} testid="admin-pc-kpi-clicks" />
            <Kpi icon={Percent} label="Click-through" value={`${t.click_through_rate ?? 0}%`} testid="admin-pc-kpi-ctr" />
            <Kpi icon={SearchX} label="Zero-result" value={t.zero_result_searches ?? 0} testid="admin-pc-kpi-zero" accent="text-amber-400" />
            <Kpi icon={Tags} label="Unique keywords" value={t.unique_keywords ?? 0} testid="admin-pc-kpi-keywords" />
            <Kpi icon={Bookmark} label="Saved products" value={t.saved_products ?? 0} testid="admin-pc-kpi-saved" />
          </div>

          <Card className="border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <h4 className="text-sm font-semibold">Daily trend</h4>
            </div>
            <TrendChart trend={data.daily_trend} />
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <PlatformSplit title="Searches by platform" data={data.searches_by_platform} testid="admin-pc-searches-platform" />
            <PlatformSplit title="Clicks by platform" data={data.clicks_by_platform} testid="admin-pc-clicks-platform" />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <RankedList
              title="Top search keywords" icon={Search} rows={data.top_keywords || []}
              emptyText="No searches yet." testid="admin-pc-top-keywords"
              renderLabel={(r) => r.query || "—"}
              renderMeta={(r) => r.zero ? <p className="text-[10px] text-amber-400">{r.zero} returned no results</p> : null}
            />
            <RankedList
              title="Zero-result keywords (demand gaps)" icon={SearchX} rows={data.zero_result_keywords || []}
              emptyText="Every search returned results — nice." testid="admin-pc-zero-keywords"
              renderLabel={(r) => r.query || "—"}
            />
            <RankedList
              title="Clicks by retailer" icon={Store} rows={data.clicks_by_retailer || []}
              emptyText="No clicks yet." testid="admin-pc-clicks-retailer"
              renderLabel={(r) => r.label || "Unknown"}
            />
            <RankedList
              title="Top clicked products" icon={MousePointerClick} rows={data.top_products || []}
              emptyText="No clicks yet." testid="admin-pc-top-products"
              renderLabel={(r) => r.title || "—"}
              renderMeta={(r) => r.retailer ? <p className="text-[10px] text-muted-foreground">{r.retailer}</p> : null}
            />
            <RankedList
              title="Most-saved products" icon={Flame} rows={data.most_saved_products || []}
              emptyText="No saved products yet." testid="admin-pc-most-saved"
              renderLabel={(r) => r.title || "—"}
              renderMeta={(r) => r.retailer ? <p className="text-[10px] text-muted-foreground">{r.retailer}</p> : null}
            />
          </div>
        </>
      )}
    </div>
  );
}
